import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { devWarn } from "@/lib/log";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading; false = unauth; obj = authed
  const [bootstrapped, setBootstrapped] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401/expired session is the expected case here — surface anything else
      if (e?.response?.status && e.response.status !== 401) devWarn("Auth bootstrap failed:", e);
      setUser(false);
    } finally {
      setBootstrapped(true);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      devWarn("Logout request failed (clearing session anyway):", e);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, refresh: fetchMe, bootstrapped }),
    [user, login, logout, fetchMe, bootstrapped]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
